import styles from "./Favourites.module.scss";
import SideBar from "../../components/sideBar/SideBar";
import React, { useEffect, useState } from "react";
import { useGetFavouritePersonsListQuery, useGetFavouritesFilmsListQuery } from "../../store/api/filmAPI";
import Footer from "../../components/footer/Footer";
import useWindowSize from "../../hooks/useWindowSize";
import Loader from "../../components/loader/Loader";
import Header from "../../components/header/Header";
import { useTranslation } from "react-i18next";
import { languageHandler } from "../../utils/helpers";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../store/store";
import MoviesList from "../search/MoviesList/MoviesList";
import ActorsList from "../search/ActorsList";
import useLikeClick from "../../hooks/useLikeClick";
import useFavouriteClick from "../../hooks/useFavouriteClick";
import useActorFavouriteClick from "../../hooks/useActorFavouriteClick";
import SavedSmall from "../../components/icons/services/SavedSmall";
import { Link } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import {
  cleanUpFavouritesPage,
  setDataToFavouritesList,
  setFavouritesPage,
  setPrevFavouritesPage,
} from "../../store/slices/favouritesList.slice";
import GradientBackdrop from "../../components/shared/GradientBackdrop/GradientBackdrop";
import LinkGuide from "../../components/shared/LinkGuide/LinkGuide";

interface IQuery {
  language: string;
  sort_by: string;
  include_video: boolean;
  "release_date_gte"?: string;
  "release_date_lte"?: string;
  search?: string;
  with_genres?: string;
}

type FavouritesTab = "movies" | "actors";

const Favourites = () => {
  const accessToken = localStorage.getItem("accessToken");
  const userData = useAppSelector((state: any) => state.auth.userData);
  const filmList = useAppSelector((state: any) => state.favouritesList.commonList);
  const page = useAppSelector((state: any) => state.favouritesList.page);
  const prevPage = useAppSelector((state: any) => state.favouritesList.prevPage);

  const dispatch = useDispatch();
  const { windowSize } = useWindowSize();
  const { t, i18n } = useTranslation();
  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();
  const { actorFavoritesList, actorFavouritesHandler } = useActorFavouriteClick();
  const [activeTab, setActiveTab] = useState<FavouritesTab>("movies");
  const [actorsPage, setActorsPage] = useState(1);
  const [actorsPrevPage, setActorsPrevPage] = useState(0);
  const [actorsList, setActorsList] = useState<any[]>([]);

  const isMoviesTab = activeTab === "movies";
  const isActorsTab = activeTab === "actors";

  const queryHandler = () => {
    const query: IQuery = {
      language: languageHandler(i18n.language) as string,
      sort_by: "popularity.desc",
      include_video: true,
    };
    return query;
  };

  const { data, isLoading, isFetching, error, refetch } = useGetFavouritesFilmsListQuery(
    {
      mainFilter: "/movies/favorites",
      queries: {
        ...queryHandler(),
        page: page,
      },
    },
    { skip: !accessToken },
  );

  const {
    data: actorsData,
    isLoading: actorsLoading,
    isFetching: actorsFetching,
    error: actorsError,
    refetch: refetchActors,
  } = useGetFavouritePersonsListQuery(
    {
      queries: {
        language: languageHandler(i18n.language) as string,
        page: actorsPage,
      },
    },
    { skip: !accessToken },
  );

  useEffect(() => {
    let timeout: any;
    if (!userData?.email && accessToken) {
      timeout = setTimeout(() => {
        refetch();
      }, 500);
    }
    return () => {
      clearTimeout(timeout);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, refetch]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((!isFetching && data && page > prevPage) || !filmList?.length) {
      dispatch(setDataToFavouritesList(data?.results));
      dispatch(setPrevFavouritesPage(page));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isFetching]);

  useEffect(() => {
    setActorsPage(1);
    setActorsPrevPage(0);
    setActorsList([]);
  }, [i18n.language]);

  useEffect(() => {
    if (!actorsFetching && actorsData?.results) {
      const responsePage = actorsData.page || actorsPage;
      if (responsePage <= actorsPrevPage) return;

      setActorsList((prevList) => {
        const nextList = responsePage === 1 ? actorsData.results : [...prevList, ...actorsData.results];
        return nextList.filter(
          (item: any, index: number, self: any[]) => index === self.findIndex((actor) => actor.id === item.id),
        );
      });
      setActorsPrevPage(responsePage);
    }
  }, [actorsFetching, actorsData, actorsPage, actorsPrevPage]);

  const setPageHandler = () => {
    dispatch(setFavouritesPage(1));
  };

  const setActorsPageHandler = () => {
    setActorsPage((currentPage) => currentPage + 1);
  };

  const handleActorFavouriteClick = async (e: any, actor: any, inFav: boolean) => {
    await actorFavouritesHandler(e, actor, inFav);

    if (inFav) {
      setActorsList((currentList) => currentList.filter((currentActor) => currentActor.id !== actor.id));
      refetchActors();
    }
  };

  useEffect(() => {
    return () => {
      dispatch(cleanUpFavouritesPage());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading || actorsLoading) {
    return <Loader />;
  }

  const moviesTotal = data?.total_results || data?.count || filmList?.length || 0;
  const actorsTotal = actorsData?.total_results || actorsData?.count || actorsList?.length || 0;
  const actorFavoriteIds = Array.isArray(actorFavoritesList?.data)
    ? actorFavoritesList.data
    : actorFavoritesList?.data?.results;
  const isLastActorsPage = actorsData?.total_pages
    ? (actorsData.page || actorsPage) === actorsData.total_pages
    : actorsData?.next === null;

  return (
    <>
      <Header className={styles.headerWrapper} />

      <GradientBackdrop/>

      {windowSize !== "mobile" ? <SideBar /> : null}
      {accessToken ? (
        <div className={styles.container}>
          <div className={`${styles.leftColumnWrapper} ${isActorsTab ? styles.leftColumnWrapperActors : ""}`}>
            <div className={styles.leftColumnTopContent}>
              <div className={styles.tabsWrapper}>
                <button
                  type="button"
                  className={`${styles.tabButton} ${isMoviesTab ? styles.tabButtonActive : ""}`}
                  onClick={() => setActiveTab("movies")}
                >
                  <span className={styles.tabTitle}>{t("movies")}</span>
                  <span className={styles.tabCounter}>
                    <b>{moviesTotal.toLocaleString("uk-UA")}</b> {t("trailers")}
                  </span>
                </button>
                <button
                  type="button"
                  className={`${styles.tabButton} ${isActorsTab ? styles.tabButtonActive : ""}`}
                  onClick={() => setActiveTab("actors")}
                >
                  <span className={styles.tabTitle}>{t("actors")}</span>
                  <span className={styles.tabCounter}>
                    <b>{actorsTotal.toLocaleString("uk-UA")}</b> {t("actor")}
                  </span>
                </button>
              </div>
              {isMoviesTab ? (
                <MoviesList
                  data={filmList}
                  error={error}
                  setPageHandler={setPageHandler}
                  isFetching={isFetching}
                  isLastData={data?.next === null}
                  likeHandler={likeHandler}
                  likesList={likesList?.data?.results}
                  favoritesList={favoritesList?.data?.results}
                  favouritesHandler={favouritesHandler}
	                  title={t("movies")}
	                  isFavoritePage={true}
	                  enableViewSwitcher={true}
	                  viewStorageKey="hiviewer.favourites.viewMode"
	                  totalCount={moviesTotal}
	                />
              ) : (
                <ActorsList
                  data={actorsList}
                  error={actorsError}
                  setPageHandler={setActorsPageHandler}
                  isFetching={actorsFetching}
                  isLastData={isLastActorsPage}
                  favoritesList={actorFavoriteIds}
                  favouritesHandler={handleActorFavouriteClick}
                  isFavoritePage={true}
                  totalCount={actorsTotal}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.unautorisedWrapper}>
          <SavedSmall />
          <div className={styles.unautorisedTitle}>{t("loginOrReg")}</div>
          <div className={styles.unautorisedText}>{t("loginOrRegText")}</div>
          <Link to={ROUTES.LOGIN}>
            <div className={styles.unautorisedBtn}>{t("auth")}</div>
          </Link>
        </div>
      )}
      <Footer source={""} />
      <LinkGuide/>
    </>
  );
};

export default Favourites;
